import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Portal,
  ClickAwayListener,
  Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LabelIcon from '@material-ui/icons/Label';
import { format, parseISO, isSameDay } from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import ShowTicketLogModal from '../../components/ShowTicketLogModal';
import TagModal from '../../components/TagModal';
import AddIcon from '@material-ui/icons/Add';
import ConnectionIcon from "../../components/ConnectionIcon";

const useStyles = makeStyles(theme => ({
  menuPaper: {
    minWidth: "300px !important",
    width: "auto !important", // Allow auto width to fit content
    maxWidth: "unset !important", // Remove max-width limit as requested
    borderRadius: "12px !important",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15) !important",
  },
  '@keyframes targetCardPulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(0, 230, 118, 0.7)',
      borderColor: '#00e676'
    },
    '50%': {
      boxShadow: '0 0 16px 4px rgba(0, 230, 118, 0.6)',
      borderColor: '#00e676'
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(0, 230, 118, 0)',
      borderColor: '#00e676'
    }
  },
  highlightedCard: {
    border: '2px solid #00e676 !important',
    animation: '$targetCardPulse 2s 4 ease-in-out',
  },
  card: {
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    background: theme.palette.background.paper,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    marginBottom: theme.spacing(1.5),
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      cursor: 'pointer',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: 1,
    gap: theme.spacing(1),
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  avatar: {
    width: 26,
    height: 26,
    fontSize: '0.8rem',
  },
  cardTitle: {
    fontSize: '0.95rem',
    fontWeight: 400,
    fontFamily: 'Roboto, sans-serif',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ticketNumber: {
    fontSize: '0.75rem',
    fontWeight: 400,
    fontFamily: 'Roboto, sans-serif',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  divider: {
    margin: theme.spacing(1, 0),
    backgroundColor: theme.palette.divider,
  },
  lastMessageTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  lastMessageTimeUnread: {
    fontSize: '0.75rem',
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  cardDescription: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    flexGrow: 1,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    lineHeight: '1.4',
  },
  valueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
  },
  descriptionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: theme.spacing(1.5),
    gap: theme.spacing(1),
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  connectionTag: {
    backgroundColor: theme.palette.grey[800],
    color: '#FFF',
    padding: '2px 8px',
    fontWeight: 500,
    borderRadius: 12,
    fontSize: '0.65rem',
    letterSpacing: '0.5px',
    fontFamily: 'Roboto, sans-serif',
  },
  opportunityValue: {
    fontSize: '0.9rem',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  removeValueButton: {
    padding: 0,
    marginLeft: theme.spacing(1),
    color: theme.palette.error.main,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
  dialogPaper: {
    borderRadius: '10px',
  },
  dialogButton: {
    borderRadius: '10px',
  },
  closedTag: {
    backgroundColor: theme.palette.error.main,
    color: '#FFF',
    padding: '2px 8px',
    fontWeight: 600,
    borderRadius: 12,
    fontSize: '0.65rem',
    letterSpacing: '0.5px',
    marginLeft: 5,
  },
  actionButtons: {
    display: 'flex',
  },
  tagChip: {
    backgroundColor: theme.palette.primary.main,
    color: '#FFF',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.65rem',
    marginRight: 2,
    marginBottom: 2,
    display: 'inline-block',
  }
}));

const KanbanCard = ({ ticket, index, updateTicket, laneTags, boardId, columnColor, laneTitle, boardLanes, laneId, handleOpenTagMenu, currency = "$", onValueChange }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isTargetTicket = searchParams.get('ticketId') === ticket.id.toString();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tagMenuPosition, setTagMenuPosition] = useState(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [currentLaneTags, setCurrentLaneTags] = useState(laneTags);
  const [expanded, setExpanded] = useState(false);

  const formatLastMessage = (msg) => {
    if (!msg) return ' ';
    if (
      /mensagem apagada/i.test(msg) ||
      /mensaje eliminado/i.test(msg) ||
      msg.includes("deletedMessage")
    ) {
      return `🚫 ${i18n.t("tickets.deletedMessage")}`;
    }
    return msg;
  };

  useEffect(() => {
    setCurrentLaneTags(laneTags);
  }, [laneTags]);

  // Removed immediate useEffect for laneTags to ensure validation against DB
  // We will load and validate tags when the menu opens.

  const fetchTags = async () => {
    try {
      // Plan 112: We rely on `boardLanes` which should now (after Backend Update) contain `kanbanLaneTags` with full tag objects.
      // We also fetch global tags (kanban=0) and board tags (kanban=1) just in case, but grouping relies on boardLanes.

      const promises = [
        api.get('/tags/list', { params: { kanban: 0 } }) // Global Labels
      ];

      if (boardId) {
        promises.push(api.get('/tags/list', { params: { kanban: 1, kanbanBoardId: boardId } }));
      }

      // Also fetch Kanban-Specific Tags (kanban: 2) for reference
      promises.push(api.get('/tags/list', { params: { kanban: 2 } }));

      const results = await Promise.all(promises);

      const globalTags = results[0]?.data || [];
      const boardTags = results[1]?.data || []; // might be undefined if no boardId
      const kanbanSpecificTags = results[2]?.data || [];

      // We don't construct a single `availableTags` list anymore for the main view.
      // Instead, we will build the groups dynamically in the render using `boardLanes` and these pools of tags.

      // Store pool for "Global" or "Other" section
      const allSystemTags = [...globalTags, ...boardTags, ...kanbanSpecificTags];
      setAvailableTags(allSystemTags);

    } catch (err) {
      console.error(err);
      toastError(err);
    }
  };

  // State for simple Sub-Tag Modal (Plan 108)
  const [addSubTagModalOpen, setAddSubTagModalOpen] = useState(false);
  const [newSubTagName, setNewSubTagName] = useState('');
  const [targetLaneIdForAdd, setTargetLaneIdForAdd] = useState(null); // Plan 112: Track which lane we are adding to

  const handleSaveSubTag = async () => {
    if (!newSubTagName.trim()) return;
    if (!laneId) {
      toast.error("Error: laneId not found");
      return;
    }

    try {
      const normalizedNewTag = newSubTagName.trim();
      let tagId;

      // 1. Find or Create the Real Tag first
      try {
        const { data } = await api.get('/tags', { params: { searchParam: normalizedNewTag } });
        const found = data.tags.find(t => t.name.toLowerCase() === normalizedNewTag.toLowerCase());

        if (found) {
          tagId = found.id;
        } else {
          const { data: created } = await api.post('/tags', {
            name: normalizedNewTag,
            color: '#555', // Default color, maybe customizable?
            kanban: 2
          });
          tagId = created.id || created.tag?.id;
        }
      } catch (e) {
        console.error("Error resolving tag ID", e);
        toast.error("Error creating tag entity");
        return;
      }

      if (!tagId) return;

      // 2. Fetch current lane to get current IDs
      const { data: laneData } = await api.get(`/tags/${laneId}`);

      // Determine current IDs
      let currentIds = [];
      if (laneData.kanbanLaneTags && laneData.kanbanLaneTags.length > 0) {
        currentIds = laneData.kanbanLaneTags.map(klt => klt.tagId);
      } else if (laneData.laneTags) {
        // Fallback: If legacy JSON exists but no relations yet (shouldn't happen after migration, but safe)
        // We might simply ignore JSON logic now and start fresh with IDs + existing?
        // No, migration ran. Trust migration or empty.
      }

      if (currentIds.includes(tagId)) {
        toast.error(i18n.t("tagsKanban.toasts.tagExists")); // "Tag already in this lane"
        return;
      }

      // 3. Update with new ID list
      // We send both laneTags (names) JSON for legacy compat AND laneTagIds for new relation
      const newIds = [...currentIds, tagId];

      const effectiveLaneId = targetLaneIdForAdd || laneId; // Use target lane if specified, else current

      await api.put(`/tags/${effectiveLaneId}`, {
        ...laneData,
        laneTagIds: newIds
      });

      toast.success(i18n.t("tagsKanban.toasts.updated"));
      setAddSubTagModalOpen(false);
      setNewSubTagName('');

      // Optimistic/Local Update? 
      // setCurrentLaneTags maps to names usually in this component.
      // But we should refresh to get new relations.

      setTimeout(() => fetchTags(), 50);

    } catch (err) {
      toastError(err);
    }
  };


  const handleCardClick = (e) => {
    // Prevent navigation if clicking on interactive elements or if text is selected
    if (e.defaultPrevented) return;
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[role="button"]')) return;
    if (window.getSelection().toString()) return;

    // Check if click comes from a portal (Menu/Dialog)
    if (tagMenuPosition || historyOpen || open || addSubTagModalOpen) return;

    history.push(`/tickets/${ticket.uuid}`);
  };

  const lastMessageTimeClass =
    Number(ticket.unreadMessages) > 0
      ? classes.lastMessageTimeUnread
      : classes.lastMessageTime;

  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;

  const handleOpenModal = () => {
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const updateContactValue = async (contactId, value) => {
    try {
      const remainingExtraInfo = (ticket.contact.extraInfo || []).filter(field => field.name !== 'valor');
      const newExtraInfo = [...remainingExtraInfo, { name: 'valor', value: value.toString() }];
      await api.put(`/contacts/${contactId}`, {
        extraInfo: newExtraInfo,
      });
    } catch (error) {
      console.error('Erro ao atualizar o valor:', error);
      throw error;
    }
  };

  const removeContactValue = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      const remainingExtraInfo = (ticket.contact.extraInfo || []).filter(field => field.name !== 'valor');
      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: remainingExtraInfo,
      });

      const updatedTicket = {
        ...ticket,
        updatedAt: new Date().toISOString(),
        contact: {
          ...ticket.contact,
          extraInfo: remainingExtraInfo
        }
      };

      updateTicket(updatedTicket);

      if (onValueChange) {
        onValueChange(ticket.id, laneId, 0);
      }

      toast.success(i18n.t("kanban.card.valueRemoved", "Valor comercial eliminado con éxito"));
    } catch (error) {
      console.error('Erro ao remover o valor:', error);
      toastError(error);
    }
  };

  const handleSaveValue = async () => {
    try {
      await updateContactValue(ticket.contact.id, newValue);

      const remainingExtraInfo = (ticket.contact.extraInfo || []).filter(field => field.name !== 'valor');
      const updatedExtraInfo = [...remainingExtraInfo, { name: 'valor', value: newValue.toString() }];

      const updatedTicket = {
        ...ticket,
        updatedAt: new Date().toISOString(),
        contact: {
          ...ticket.contact,
          extraInfo: updatedExtraInfo
        }
      };

      updateTicket(updatedTicket);

      if (onValueChange) {
        onValueChange(ticket.id, laneId, parseFloat(newValue) || 0);
      }

      toast.success(i18n.t("kanban.card.valueSaved", "Valor comercial guardado con éxito"));
      setOpen(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleHistoryOpen = (e) => {
    e.stopPropagation();
    setHistoryOpen(true);
  };

  const handleHistoryClose = () => {
    setHistoryOpen(false);
  };

  /* Control de Menú de Etiquetas (Ahora delegado al padre) */
  const handleTagMenuOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleOpenTagMenu) {
      handleOpenTagMenu({
        position: { top: e.clientY, left: e.clientX },
        ticket: ticket,
        laneId: laneId
      });
    }
  };

  const handleTagMenuClose = () => {
    setTagMenuPosition(null);
  };



  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          id={`kanban-ticket-${ticket.id}`}
          className={`${classes.card} ${ticket.imported ? classes.importedCard : ''} ${isTargetTicket ? classes.highlightedCard : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}

          onMouseLeave={() => setExpanded(false)}
        >
          {ticket.imported && (
            <Tooltip title="Contacto Importado">
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  borderWidth: '0 25px 25px 0',
                  borderColor: 'transparent #ffb74d transparent transparent',
                  zIndex: 10,
                }}
              />
            </Tooltip>
          )}
          <div className={classes.header}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              {/* Row 0: Conexión (Arriba del círculo del contacto) */}
              {ticket.channel && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, gap: 5 }}>
                  <ConnectionIcon
                    connectionType={ticket?.whatsapp?.channel || ticket.channel}
                    style={{ marginBottom: 0, fontSize: 16 }}
                  />
                  <Typography
                    variant="caption"
                    style={{
                      color: '#a0a0a0',
                      fontSize: '0.68rem',
                      fontWeight: 500,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ticket.whatsapp?.name || ticket.channel}
                  </Typography>
                </div>
              )}

              {/* Row 1: Avatar + Name + Phone */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5, width: '100%' }}>
                <Avatar src={ticket.contact.urlPicture} className={classes.avatar} />
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 8, minWidth: 0, flex: 1 }}>
                  <Tooltip title={ticket.contact.name}>
                    <Typography className={classes.cardTitle} style={{ lineHeight: 1.2 }}>
                      {ticket.contact.name || ' '}
                    </Typography>
                  </Tooltip>
                  {ticket.contact.number && (
                    <Typography variant="caption" style={{ color: '#aaa', fontSize: '0.7rem', marginTop: 1 }}>
                      {ticket.contact.number}
                    </Typography>
                  )}
                </div>
              </div>

              {/* Row 2: ID + Icons */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 2 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography className={classes.ticketNumber} style={{ fontSize: '0.65rem' }}>
                    Ticket: #{ticket.id}
                  </Typography>
                  <Typography className={classes.ticketNumber} style={{ fontSize: '0.65rem' }}>
                    Cod. Cliente: {ticket.contact.id}
                  </Typography>
                </div>

                <div style={{ display: 'flex' }}>
                  {/* Eye Icon (History) */}
                  <VisibilityIcon
                    fontSize="small"
                    style={{ color: '#999', cursor: 'pointer', marginRight: 5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHistoryOpen(e);
                    }}
                  />

                  {/* Tag Icon (Menu) */}
                  <div
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagMenuOpen(e);
                    }}
                  >
                    <LabelIcon fontSize="small" style={{ color: '#999' }} />
                  </div>
                </div>
              </div>

              {ticket.status === 'closed' && (
                <div style={{ marginTop: 5 }}>
                  <span className={classes.closedTag} style={{ marginLeft: 0 }}>CERRADO</span>
                </div>
              )}

              {/* Tags Block - Rendered directly under the info */}
              <div style={{ marginTop: 4 }}>
                {(() => {
                  // Plan 112: Grouped Tags Display in Card Face
                  // Use local state (optimistic) for real-time reactivity
                  const currentTags = ticket.tags || [];
                  
                  // [ANTIGRAVITY] FOOLPROOF EXCLUSION:
                  // Collect all tag IDs that act as Kanban Columns (Stages)
                  const stageTagIds = new Set();
                  (boardLanes || []).forEach(lane => {
                    if (lane && lane.id) {
                      stageTagIds.add(lane.id);
                      stageTagIds.add(String(lane.id));
                      stageTagIds.add(Number(lane.id));
                    }
                  });

                  // Filter out ANY tag that is marked as kanban=1 OR whose ID matches a stage tag
                  const tagsToDisplay = currentTags.filter(t => 
                    Number(t.kanban) !== 1 && String(t.kanban) !== "1" && t.kanban !== true && !stageTagIds.has(t.id) && !stageTagIds.has(String(t.id))
                  );

                  if (tagsToDisplay.length === 0) return null;

                  // Lógica de Agrupación (FIX: Group by ID for unique tags)
                  let tagIdToLanesMap = {};
                  (boardLanes || []).forEach(lane => {
                    const processTag = (tagId, laneTitle) => {
                      const key = String(tagId);
                      if (!tagIdToLanesMap[key]) tagIdToLanesMap[key] = [];
                      if (!tagIdToLanesMap[key].includes(laneTitle)) {
                        tagIdToLanesMap[key].push(laneTitle);
                      }
                    };

                    if (lane.kanbanLaneTags && Array.isArray(lane.kanbanLaneTags)) {
                      lane.kanbanLaneTags.forEach(klt => {
                        if (klt.tag && klt.tag.id) processTag(klt.tag.id, lane.title);
                      });
                    }

                    // Legacy JSON support (names only) - hard to map by ID without lookup
                    // If we have legacy tags as strings, we fallback to name matching or ignore?
                    // Given we are moving to IDs, we should try to match IDs if possible.
                    // But if laneTags is just strings, we can't map by ID easily. 
                    // Let's keep name-based fallback? No, user wants strict separation.
                    // If legacy tags are strings, they are likely shared tags anyway.
                    // Let's support ID mapping primarily.
                  });

                  // Build Grouped Object
                  const grouped = {};
                  tagsToDisplay.forEach(tag => {
                    // Try ID first
                    const key = String(tag.id);
                    const laneTitles = tagIdToLanesMap[key];

                    if (laneTitles && laneTitles.length > 0) {
                      laneTitles.forEach(title => {
                        if (!grouped[title]) grouped[title] = [];
                        grouped[title].push(tag);
                      });
                    } else {
                      // Fallback: If not found by ID (maybe legacy name match?)
                      // If we strict map by ID, "Urgente" (101) won't show under Stage 2 (which has "Urgente" 102).
                      // This is EXACTLY what we want.

                      // Fallback for tags not in any lane configuration
                      if (!grouped['__OTHER__']) grouped['__OTHER__'] = [];
                      grouped['__OTHER__'].push(tag);
                    }
                  });

                  const sortedGroupTitles = (boardLanes || []).map(l => l.title);
                  if (grouped['__OTHER__']) sortedGroupTitles.push('__OTHER__');

                  const visibleTitles = expanded
                    ? sortedGroupTitles
                    : sortedGroupTitles.filter(t => t === laneTitle || t === '__OTHER__'); // Current lane + explicit others if needed, usually just Current

                  // If we are collapsed, check if there are any hidden groups with items
                  const hasHiddenItems = !expanded && sortedGroupTitles.some(title => {
                    // If title is NOT current lane AND it has items in grouped
                    return title !== laneTitle && grouped[title] && grouped[title].length > 0;
                  });

                  return (
                    <>
                      {visibleTitles.filter(title => grouped[title] && grouped[title].length > 0).map(groupTitle => {
                        const lane = (boardLanes || []).find(l => l.title === groupTitle);
                        const groupColor = lane ? lane.color : null;

                        // [ANTIGRAVITY] Sort tags within the group based on Lane Configuration Order
                        const laneTagsOrdered = lane && lane.kanbanLaneTags ? lane.kanbanLaneTags : [];
                        const tagsInGroup = grouped[groupTitle];

                        tagsInGroup.sort((a, b) => {
                          const indexA = laneTagsOrdered.findIndex(klt => klt.tag && klt.tag.id === a.id);
                          const indexB = laneTagsOrdered.findIndex(klt => klt.tag && klt.tag.id === b.id);

                          // If both found, sort by index
                          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                          // If only A found, A comes first
                          if (indexA !== -1) return -1;
                          // If only B found, B comes first
                          if (indexB !== -1) return 1;
                          // If neither found, sort by ID (fallback)
                          return a.id - b.id;
                        });

                        return (
                          <div key={groupTitle} style={{ marginTop: 8, marginBottom: 4 }}>
                            {groupTitle !== '__OTHER__' && (
                              <Typography variant="caption" style={{
                                fontWeight: 400,
                                fontFamily: 'Roboto, sans-serif',
                                color: '#bbb',
                                display: 'block',
                                textTransform: 'uppercase',
                                fontSize: '0.65rem',
                                letterSpacing: '0.5px',
                                marginBottom: 2,
                                borderBottom: `1px solid ${groupColor || '#eee'}`
                              }}>
                                {groupTitle}
                              </Typography>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: 2 }}>
                              {grouped[groupTitle].map(tag => (
                                <span
                                  key={tag.id}
                                  className={classes.tagChip}
                                  style={{
                                    backgroundColor: groupColor || tag.color || '#555',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '0.65rem',
                                    fontWeight: 300,
                                    letterSpacing: '0.4px',
                                    fontFamily: 'Roboto, sans-serif',
                                    margin: '1px'
                                  }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                      {hasHiddenItems && (
                        <div
                          style={{ textAlign: 'center', marginTop: 4, cursor: 'pointer', color: '#999' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(true);
                          }}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>


          </div>

          <Divider className={classes.divider} />

          <div className={classes.valueRow}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                className={classes.opportunityValue}
                onClick={handleOpenModal}
              >
                {opportunityValue !== null
                  ? `${i18n.t('kanban.card.value')}: ${currency} ${opportunityValue.toFixed(2)}`
                  : i18n.t('kanban.card.assignValue')}
              </Typography>
              {opportunityValue !== null && (
                <Tooltip title={i18n.t('tagsKanban.confirmationModal.deleteTitle', 'Eliminar')}>
                  <IconButton
                    className={classes.removeValueButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeContactValue(e);
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          <div className={classes.descriptionRow}>
            {/* Plan 112: Grouped Tags Display in Card Face */}
            {/* We already have this logic below, but we should ensure it uses the Refactored Grouping if we want consistency */}
            {/* For now, the existing render is fine for the CARD FACE. The Plan 112 focuses on the MENU (Editing). */}

            <Tooltip title={formatLastMessage(ticket.lastMessage)}>
              <Typography className={classes.cardDescription}>
                {formatLastMessage(ticket.lastMessage)}
              </Typography>
            </Tooltip>
            <Typography className={lastMessageTimeClass}>
              {ticket.updatedAt ?
                (isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), 'HH:mm')
                  : format(parseISO(ticket.updatedAt), 'dd/MM/yyyy'))
                : ''}
            </Typography>
          </div>

          <div className={classes.footer} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            {ticket.user && (
              <Typography className={classes.connectionTag}>
                {ticket.user.name.toUpperCase()}
              </Typography>
            )}
            <Button
              size="small"
              variant="contained"
              color="primary"
              style={{
                height: 24,
                fontSize: '0.65rem',
                fontWeight: 500,
                minWidth: '60px',
                padding: '0 8px',
                textTransform: 'none',
                alignSelf: 'flex-end', // Align button to right
                marginTop: 4 // Space from user info
              }}
              onClick={() => history.push(`/tickets/${ticket.uuid}`)}
              startIcon={<OpenInNewIcon style={{ fontSize: 13 }} />}
            >
              {i18n.t('kanban.buttons.open')}
            </Button>

          </div>


          <TagModal
            open={tagModalOpen}
            onClose={() => {
              setTagModalOpen(false);
              fetchTags(); // Refresh tags after create
            }}
            kanban={0} // Create a label/tag, not a stage
          />

          <Dialog open={addSubTagModalOpen} onClose={() => setAddSubTagModalOpen(false)}>
            <DialogTitle>Nueva etiqueta en etapa</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Nombre de la etiqueta"
                fullWidth
                value={newSubTagName}
                onChange={(e) => setNewSubTagName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddSubTagModalOpen(false)} color="secondary">
                Cancelar
              </Button>
              <Button onClick={handleSaveSubTag} color="primary">
                Guardar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={open}
            onClose={handleCloseModal}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>{valueField ? i18n.t('kanban.card.editValueTitle') : i18n.t('kanban.card.assignValueTitle')}</DialogTitle>
            <form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveValue();
              }}
            >
              <DialogContent>
                <TextField
                  id="opportunity-value-amount"
                  name="opportunity-value-amount"
                  label={i18n.t('kanban.card.value')}
                  type="number"
                  fullWidth
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                  autoComplete="off"
                  inputProps={{
                    autoComplete: 'off',
                    'data-lpignore': 'true',
                    'data-form-type': 'other',
                    step: 'any',
                    min: '0'
                  }}
                  autoFocus
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseModal}
                  color="secondary"
                  variant="outlined"
                  className={classes.dialogButton}
                  type="button"
                >
                  {i18n.t('kanban.card.cancel')}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="outlined"
                  className={classes.dialogButton}
                >
                  {i18n.t('kanban.card.save')}
                </Button>
              </DialogActions>
            </form>
          </Dialog>

          <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}>
            <ShowTicketLogModal
              isOpen={historyOpen}
              handleClose={handleHistoryClose}
              ticketId={ticket.id}
            />
          </div>
        </div >
      )
      }
    </Draggable >
  );
};

const areEqual = (prevProps, nextProps) => {
  const prevExtra = prevProps.ticket?.contact?.extraInfo || [];
  const nextExtra = nextProps.ticket?.contact?.extraInfo || [];
  const prevVal = prevExtra.find(f => f.name === 'valor')?.value;
  const nextVal = nextExtra.find(f => f.name === 'valor')?.value;

  return (
    prevProps.ticket.id === nextProps.ticket.id &&
    prevProps.ticket.updatedAt === nextProps.ticket.updatedAt &&
    prevProps.ticket.status === nextProps.ticket.status &&
    prevProps.ticket.unreadMessages === nextProps.ticket.unreadMessages &&
    prevVal === nextVal &&
    prevProps.currency === nextProps.currency
  );
};

export default React.memo(KanbanCard, areEqual);
